import { Controller, FieldValues } from 'react-hook-form';
import { cn } from '@/lib';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui';
import { SelectFieldProps } from '@/types';

export function SelectField<TFormData extends FieldValues = Record<string, unknown>>({
  label,
  required,
  error,
  placeholder,
  name,
  control,
  options,
  className = '',
  description,
  disabled,
  rules,
}: SelectFieldProps<TFormData>) {
  // Bind the label to the Radix trigger — it's a div, not a native <select>, so
  // htmlFor needs an explicit id to match. Screen readers otherwise announce the
  // control unlabelled, which matters most where the label is dynamic (the
  // gift-card-aware payment method field changes it as coverage changes).
  const triggerId = `select-${String(name)}`;

  return (
    <div className={className}>
      <label htmlFor={triggerId} className="block text-sm font-medium text-foreground mb-2">
        {label} {required && <span className="text-destructive">*</span>}
      </label>
      <Controller<TFormData>
        name={name}
        control={control}
        // `disabled` must reach RHF, not just the rendered <Select>. RHF skips
        // validation for fields it knows are disabled and omits them from the
        // submitted values — which is how a conditionally-inapplicable field
        // (e.g. payment method when a gift card covers the full price) gets
        // exempted. Do NOT express that by swapping `rules`: useController
        // captures rules in a useRef on the first render and never re-reads
        // them, and register() shallow-merges, so a rule dropped after mount
        // keeps being enforced. Value shown in the select is preserved either
        // way — RHF only tracks the name, it doesn't blank the form value.
        disabled={disabled}
        rules={rules}
        render={({ field }) => (
          <Select
            value={String(field.value || '')}
            onValueChange={field.onChange}
            disabled={disabled}
          >
            <SelectTrigger id={triggerId} className={cn(error && "border-destructive")}>
              <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent>
              {options.map((option) => (
                <SelectItem key={option.value} value={String(option.value)}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      />
      {error && (
        <p className="mt-2 text-sm text-destructive">{error}</p>
      )}
      {description && (
        <p className="mt-2 text-sm text-muted-foreground">{description}</p>
      )}
    </div>
  );
} 
